import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Form, FormControl, FormSelect, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUser, faTag, faClock, faEye } from '@fortawesome/free-solid-svg-icons';
import { 
    fetchNotifications, 
    fetchUnreadCount, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification 
} from './notificationsSlice';
import { client } from '../../api/client';
import RichTextEditor from '../policy/RichTextEditor';
import CreateNotificationModal from './CreateNotificationModal';
import NotificationDetailModal from './NotificationDetailModal';
import { globalConstants } from '../../constants/global.constants';
import styles from './NotificationPage.module.scss';

export const NotificationPage = () => {
    const dispatch = useDispatch();
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const [search, setSearch] = useState('');
    const [sender, setSender] = useState('');
    const [type, setType] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    
    const { 
        notifications, 
        status, 
        unreadCount, 
        pagination 
    } = useSelector(state => state.notifications);
    const user = useSelector((state) => state.authentication?.user);

    // Keep a stable list of all senders so selecting a sender won't remove other options
    const [allSenders, setAllSenders] = useState([]);

    // Use fixed notification types (matching backend Helper.php)
    const NOTIFICATION_TYPES = ['Reminder', 'Urgent Update', 'Operation', 'Weekly Update'];
    const types = NOTIFICATION_TYPES;

    useEffect(() => {
        // Use an async function so we can capture the initial fetch result and
        // populate the stable list of senders once (do not overwrite on later filters).
        const load = async () => {
            const action = await dispatch(fetchNotifications({
                perPage: 20,
                unreadOnly: showUnreadOnly,
                search,
                sender,
                type,
                dateFrom,
                dateTo
            }));

            // Populate allSenders only once (when it's empty) using returned payload
            try {
                if (allSenders.length === 0 && action && action.payload && Array.isArray(action.payload.notifications)) {
                    const senderSet = new Map();
                    action.payload.notifications.forEach(notif => {
                        if (notif.creator) {
                            const name = `${notif.creator.first_name || ''} ${notif.creator.last_name || ''}`.trim() || notif.creator.email || 'Unknown';
                            senderSet.set(notif.creator.id, { id: notif.creator.id, name });
                        }
                    });
                    setAllSenders(Array.from(senderSet.values()).sort((a, b) => a.name.localeCompare(b.name)));
                }
            } catch (e) {
                // ignore
            }

            dispatch(fetchUnreadCount());
        };

        load();
    }, [dispatch, showUnreadOnly, search, sender, type, dateFrom, dateTo]); 

    const handleMarkAsRead = async (notificationId) => {
        await dispatch(markAsRead(notificationId));
        dispatch(fetchUnreadCount());
    };

    const handleMarkAllAsRead = async () => {
        await dispatch(markAllAsRead());
        dispatch(fetchNotifications({ perPage: 20, unreadOnly: showUnreadOnly }));
        dispatch(fetchUnreadCount());
    };

    const handleDelete = async (notificationId) => {
        await dispatch(deleteNotification(notificationId));
        dispatch(fetchNotifications({ perPage: 20, unreadOnly: showUnreadOnly }));
        dispatch(fetchUnreadCount());
    };

    const handleToggleFilter = () => {
        setShowUnreadOnly(!showUnreadOnly);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'policy_created':
                return '📝';
            case 'policy_updated':
                return '✏️';
            case 'success':
                return '✅';
            case 'warning':
                return '⚠️';
            case 'error':
                return '❌';
            default:
                return '🔔';
        }
    };

    const getNotificationVariant = (type) => {
        switch (type) {
            case 'policy_created':
                return 'success';
            case 'policy_updated':
                return 'info';
            case 'warning':
                return 'warning';
            case 'error':
                return 'danger';
            default:
                return 'primary';
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
        
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const handleClearFilters = () => {
        setSearch('');
        setSender('');
        setType('');
        setDateFrom('');
        setDateTo('');
        setShowUnreadOnly(false);
    };

    // Create notification modal state (component handles form state)
    const [showCreateModal, setShowCreateModal] = useState(false);
    const openCreate = () => setShowCreateModal(true);
    const closeCreate = () => setShowCreateModal(false);

    const onCreated = (data) => {
        dispatch(fetchNotifications({ perPage: 20, unreadOnly: showUnreadOnly, search, sender, type, dateFrom, dateTo }));
        dispatch(fetchUnreadCount());
    };

    // Detail modal state
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const openDetail = (notification) => {
        setSelectedNotification(notification);
        setShowDetailModal(true);
    };
    const closeDetail = () => {
        setSelectedNotification(null);
        setShowDetailModal(false);
    };

    const hasActiveFilters = search || sender || type || dateFrom || dateTo || showUnreadOnly;

    return (
        <Container fluid className={styles.notificationPage}>
            <Row className="mb-4">
                <Col>
                    <div className={styles.pageHeader}>
                        <h2>
                            {/* <span className={styles.icon}>🔔</span> */}
                            Notifications &nbsp;
                            {unreadCount > 0 && (
                                <Badge bg="danger" className="ms-3 text-white">
                                    {unreadCount} unread
                                </Badge>
                            )}
                        </h2>
                        <div className={styles.headerActions}>
                            { (user?.is_admin || user?.is_admin_or_qa) && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={openCreate}
                                    className="me-2"
                                >
                                    Create Notification
                                </Button>
                            )}
                            {unreadCount > 0 && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleMarkAllAsRead}
                                >
                                    Mark All as Read
                                </Button>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Filters Row */}
            <Row className="mb-4">
                <Col>
                    <div className={styles.filtersRow}>
                        {/* Search */}
                        <div className={styles.filterItem}>
                            <FontAwesomeIcon icon={faSearch} className={styles.filterIcon} />
                            <FormControl
                                type="text"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className={styles.filterInput}
                            />
                        </div>

                        {/* Sender Filter */}
                        <div className={styles.filterItem}>
                            <FontAwesomeIcon icon={faUser} className={styles.filterIcon} />
                            <FormSelect
                                value={sender}
                                onChange={(e) => setSender(e.target.value)}
                                className={styles.filterSelect}
                            >
                                <option value="">Sender</option>
                                {allSenders.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </FormSelect>
                        </div>

                        {/* Type/Tag Filter */}
                        <div className={styles.filterItem}>
                            <FontAwesomeIcon icon={faTag} className={styles.filterIcon} />
                            <FormSelect
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                className={styles.filterSelect}
                            >
                                <option value="">Tag</option>
                                {types.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </FormSelect>
                        </div>

                        {/* Send At Date Filter - From */}
                        <div className={styles.filterItem}>
                            <FontAwesomeIcon icon={faClock} className={styles.filterIcon} />
                            <FormControl
                                type="date"
                                placeholder="Send at (from)"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className={styles.filterInput}
                                title="Date from"
                            />
                        </div>

                        {/* Send At Date Filter - To */}
                        <div className={styles.filterItem}>
                            <FormControl
                                type="date"
                                placeholder="Send at (to)"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className={styles.filterInput}
                                title="Date to"
                            />
                        </div>

                        {/* Unread Only Toggle */}
                        <div className={styles.filterItem}>
                            <Form.Check
                                type="switch"
                                id="unread-only-switch"
                                label="Unread Only"
                                checked={showUnreadOnly}
                                onChange={(e) => setShowUnreadOnly(e.target.checked)}
                                className={styles.unreadToggle}
                            />
                        </div>

                        {hasActiveFilters && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleClearFilters}
                                className={styles.clearFiltersBtn}
                            >
                                Clear Filters
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Results Count */}
            {pagination.total > 0 && (
                <Row className="mb-3">
                    <Col>
                        <p className={styles.resultsCount}>
                            You have {pagination.total} result{pagination.total !== 1 ? 's' : ''} matched
                        </p>
                    </Col>
                </Row>
            )}

            <CreateNotificationModal show={showCreateModal} onClose={closeCreate} onCreated={onCreated} />
            <NotificationDetailModal show={showDetailModal} onClose={closeDetail} notification={selectedNotification} />

            {status === globalConstants.LOADING && notifications.length === 0 ? (
                <Row>
                    <Col className="text-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden"></span>
                        </Spinner>
                        <p className="mt-3">Loading notifications...</p>
                    </Col>
                </Row>
            ) : notifications.length === 0 ? (
                <Row>
                    <Col>
                        <Alert variant="info" className={styles.emptyState}>
                            <h4>No notifications</h4>
                            <p>
                                {showUnreadOnly 
                                    ? "You have no unread notifications. You're all caught up!" 
                                    : "You don't have any notifications yet."}
                            </p>
                        </Alert>
                    </Col>
                </Row>
            ) : (
                <Row>
                    <Col>
                        <div className={styles.notificationsList}>
                            {notifications.map((notification) => (
                                <Card 
                                    key={notification.id} 
                                    className={`${styles.notificationCard} ${!notification.is_read ? styles.unread : ''}`}
                                    onClick={() => openDetail(notification)}
                                >
                                    <Card.Body>
                                        <div className={styles.notificationContent}>
                                            {/* <div className={styles.notificationIcon}>
                                                {getNotificationIcon(notification.type)}
                                            </div> */}
                                            <div className={styles.notificationDetails}>
                                                <div className={styles.notificationHeader}>
                                                    <h5 className={styles.notificationTitle}>
                                                        {notification.title}
                                                        <Badge 
                                                            bg={getNotificationVariant(notification.type)}
                                                            className="ms-2 text-white"
                                                        >
                                                            {notification.type.replace('_', ' ')}
                                                        </Badge>
                                                        {!notification.is_read && (
                                                            <Badge bg="danger" className="ms-2 text-white">
                                                                New
                                                            </Badge>
                                                        )}
                                                    </h5>
                                                </div>
                                                <div className={styles.notificationTime}>
                                                    {notification.creator && (
                                                        <span className={styles.senderInfo}>
                                                            From: {notification.creator.first_name || ''} {notification.creator.last_name || ''} {notification.creator.email && (<>&lt;{notification.creator.email}&gt;</>)}
                                                        </span>
                                                    )}
                                                    <span className="ms-2">{formatDate(notification.created_at)}</span>
                                                </div>
                                                {/* <div className={styles.notificationMessage}>
                                                    <div dangerouslySetInnerHTML={{ __html: notification.message }} />
                                                </div> */}
                                                {notification.entity_data && (
                                                    <div className={styles.entityData}>
                                                        {notification.entity_data.policy_name && (
                                                            <div className={styles.policyInfo}>
                                                                <strong>Policy:</strong> {notification.entity_data.policy_name}
                                                                {notification.entity_data.policy_code && (
                                                                    <span className={styles.policyCode}>
                                                                        ({notification.entity_data.policy_code})
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {notification.entity_data.policy_definition && (
                                                            <div className={styles.policyDefinition}>
                                                                <strong>Definition:</strong>
                                                                <div dangerouslySetInnerHTML={{ __html: notification.entity_data.policy_definition }} />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                <div className={styles.notificationActions}>
                                                    {!notification.is_read && (
                                                        <Button 
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleMarkAsRead(notification.id)}
                                                        >
                                                            Mark as Read
                                                        </Button>
                                                    )}
                                                    {/* <Button 
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(notification.id)}
                                                    >
                                                        Delete
                                                    </Button> */}
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>

                        {pagination.lastPage > 1 && (
                            <div className={styles.pagination}>
                                <p className="text-muted">
                                    Showing page {pagination.currentPage} of {pagination.lastPage} 
                                    ({pagination.total} total notifications)
                                </p>
                            </div>
                        )}
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default NotificationPage;

